<script>
import { mapGetters } from 'vuex'
import CalendarResource from '@/resources/calendar'
import ReservationResource from '@/resources/reservation'
import Checkout from '../components/Checkout.vue'
import CalendarReservationsSimple from '../components/CalendarReservationsSimple.vue'
import messages from '@/mixins/messages'
import moment from 'moment'
import { TIMEZONE } from '@/config'
import ResourceAttribute from '../components/ResourceAttribute.vue'
import { timeDateFormattedPretty, dateFormattedPretty } from '@/util'
import WaiverModal from '../components/WaiverModal.vue'

export default {
    name: 'ReservationsCreate',
    mixins: [messages],
    components: {
        Checkout,
        CalendarReservationsSimple,
        ResourceAttribute,
        WaiverModal
    },
    computed: {
        ...mapGetters(['userResource']),
        bookDateFormattedPretty() {
            return moment(this.bookDate).format('dddd, MMMM Do')
        },
        bookDateFormatted() {
            return moment(this.bookDate).format('YYYY-MM-DD')
        },
        bookTimeDateFormattedPretty() {
            if(!this.bookTimeSlotResource) {
                return this.bookDateFormattedPretty
            }
            return moment(this.bookTimeSlotResource.dateTime).format('dddd, MMMM Do \@ h:mm a')
        },
        canBookWithSelectedCar() {
            return this.bookTimeSlotResource && this.bookSubscriptionResource && parseInt(this.bookSubscriptionResource.get('reservations_remaining')) > 0
        },
        reservationResourcesAvailable() {
            let now = new Date()
            return this.reservationResources.filter(resource => {
                let dt = resource.dateTime
                return dt > now && ~[0, 30].indexOf(dt.getMinutes())
            })
        },
        calendarWrapperClasses() {
            if (this.calendarForceVisible) {
                return 'd-block d-lg-none'
            } else {
                return 'd-none d-lg-block'
            }
        }
    },
    data() {
        const now = new Date()
        const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const minDate = new Date(now.getFullYear(), now.getMonth() - 2, 0)
        const maxDate = new Date(now.getFullYear(), now.getMonth() + 2, 0)
        return {
            calendarResource: null,
            calendarResourceImageURL: null,
            calendarForceVisible: false,
            reservationResources: [],
            loading: true,
            loadingReservationTimes: false,
            bookDate: today,
            bookTimeSlotResource: null,
            bookSubscriptionResource: null,
            dateMin: minDate,
            dateMax: maxDate,
            tz: TIMEZONE,
            bookModalSize: 'lg',
        }
    },
    methods: {
        timeDateFormattedPretty,
        dateFormattedPretty,
        async bookDateChange() {
            this.bookTimeSlotResource = null
        },
        forceCalendarOpen() {
            this.calendarForceVisible = true
            window.scroll(0, 0)
        },
        async onBookingCreate(commitData) {
            this.$refs.bookModal.hide()
            this.$analytics.track('created_reservation')

            if (this.$refs.waiverModal.hasOutstanding()) {
                this.$refs.waiverModal.next()
            } else if (commitData.redirect) {
                window.location.href = commitData.redirect
            } else {
                this.$router.push({ name: 'calendarControlWaitingRoom', params: { calendarId: this.calendarResource.id } })
            }
        },
        onBookingError() {

        },
        onCalendarSelect(calendar, reservations) {
            this.reservationResources = reservations
        },
        onCheckout() {
            this.$refs.bookModal.show()
        }
    },
    async mounted() {
        this.loading = true

        let [userReservationResources, calendarResource] = await Promise.all([
            ReservationResource.filterAll({ user: this.userResource.id, group_base: true, future: true }),
            CalendarResource.detail(this.$route.params.calendarId),
        ])

        this.userReservationResources = userReservationResources
        this.calendarResource = calendarResource
        this.calendarResourceImageURL = await this.calendarResource.resolveAttribute('image.url')
        this.loading = false

        this.userReservationResources.forEach(resource => resource.resolveRelated('subscription'))

        this.$nextTick(() => {
            this.$refs.calendar.$emit('input', this.bookDate)
            addeventatc.refresh()
        })
    },
}
</script>

<template>
    <div>
        <header-image kicker="Réserver un simulateur">
            <h1>Réservations</h1>
        </header-image>

        <b-container class="mt-3" fluid="lg" v-if="!loading">
            <b-alert v-if="!calendarForceVisible" show variant="info" class="d-block d-lg-none">
                <strong>Date sélectionnée :</strong> {{ dateFormattedPretty(bookDate) }} (<a href="javascript:{}" @click="forceCalendarOpen()">modifier</a>)
                <b-link :to="{ name: 'reservationsAdvanced', query: { location: calendarResource.attributes.location } }">voir les autres simulateurs</b-link>
            </b-alert>
            
            <b-row>
                <b-col md="4" offset-md="2" class="mt-2">
                    <b-card bg-variant="light" :class="calendarWrapperClasses">
                        <b-calendar v-model="bookDate" block value-as-date :hide-header="true" @input="bookDateChange" ref="calendar" selected-variant="success" today-variant="info" locale="en" :min="dateMin" :max="dateMax"></b-calendar>
                        <hr />
                        <b-button variant="primary" @click="onCheckout()" :disabled="reservationResources.length < 1" class="col-sm-12 mb-2">Passer au paiement</b-button>
                    </b-card>
                </b-col>

                <b-col sm="4" class="mt-2 mb-5">
                    <div v-if="messages.length > 0">
                        <b-alert :variant="message.variant" show v-for="(message, i) in messages" :key="i">{{ message.text }}</b-alert>
                    </div>

                    <b-card :img-src="calendarResourceImageURL" img-top>
                        <calendar-reservations-simple
                            :calendar-id="calendarResource.id"
                            :for-date="bookDate"
                            @input="(reservations) => onCalendarSelect(calendarResource, reservations)"
                            many>
                            <template slot="calendar-body" slot-scope="data">
                                <b-button variant="primary" size="sm" block @click="forceCalendarOpen()" v-if="data.reservations.length == 0" class="d-block d-lg-none">Modifier la date sélectionnée</b-button>
                            </template>
                        </calendar-reservations-simple>
                    </b-card>

                    <b-button :to="{ name: 'reservationsAdvanced', query: { location: calendarResource.attributes.location } }" variant="primary" class="col-md-12 mt-3 mb-5">Voir les autres simulateurs</b-button>
                </b-col>
            </b-row>
        </b-container>
        
        <b-container class="mt-3 d-block d-lg-none pt-3" id="taco" v-if="reservationResources.length > 0"  fluid>
            <b-row>
                <b-col class="text-center text-muted">
                    <b-button variant="primary" @click="onCheckout()" class="col-sm-12">Passer au paiement</b-button>
                    <small>{{ (reservationResources.length === 1) ? '1 créneau sélectionné' : `${reservationResources.length} créneaux sélectionnés` }} avec 1 simulateur</small>
                </b-col>
            </b-row>
        </b-container>

        <b-modal ref="bookModal" :size="bookModalSize" title="Paiement" hide-footer no-fade centered>
            <checkout
                :reservation-resources="reservationResources"
                @size="(newSize) => bookModalSize = newSize"
                @success="(commitData) => onBookingCreate(commitData)"
                @error="onBookingError"
                use-payment-link>
            </checkout>
        </b-modal>

        <b-modal ref="completeModal" title="Terminé !" ok-title="Se déconnecter" cancel-title="Réserver à nouveau" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
            <h3>Simulateur réservé avec succès</h3>
            <p>Que souhaitez-vous faire ensuite ?</p>
        </b-modal>

        <b-modal ref="errorModal" title="Erreur de réservation" ok-title="Vérifier le mode de paiement" cancel-title="Retour" cancel-variant="primary" no-fade no-close-on-backdrop no-close-on-esc hide-header-close centered>
            <h3>Une erreur est survenue lors de la réservation</h3>
            <b-alert variant="danger" show>Votre réservation n’a pas pu être finalisée en raison de problèmes lors du traitement de votre paiement.</b-alert>
        </b-modal>

        <waiver-modal ref="waiverModal" @success="onBookingCreate" />
    </div>
</template>

<style lang="scss">
#taco {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    z-index: 999;
    background-color: var(--panel);
    color: var(--ink);
    border-top: 1px solid var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.28);
    padding-bottom: 1rem;
}
:root[data-theme='dark'] #taco {
    background-color: var(--panel-2);
    border-top-color: var(--line-2);
    box-shadow: 0 -10px 30px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255, 255, 255, 0.05);
}
</style>
